/**
 * Renders a non-interactive image preview with optional caption.
 *
 * Supports image URLs and base64 data URIs.
 * Falls back to alt text if the image fails to load.
 * Constrains image width via optional `maxWidth` payload field.
 *
 * Uses Deckard's theme palette tokens for proper light/dark mode support.
 */
import { Typography } from '@components/typography';
import { Box, Stack, useTheme } from '@mui/material';
import { useState } from 'react';
import type { RichBlockProps } from './richBlockTypes';

export const ImagePreviewBlock = ({ block }: RichBlockProps) => {
  const { palette } = useTheme();
  const [hasError, setHasError] = useState(false);
  const {
    src,
    alt = 'Image preview',
    caption,
    maxWidth = 480,
  } = block.payload as {
    src: string;
    alt?: string;
    caption?: string;
    maxWidth?: number;
  };

  return (
    <Stack mt={1} mb={2} gap={1} data-testid="rich-block-image-preview">
      <Box
        sx={{
          border: '1px solid',
          borderColor: palette.input.border.default,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: palette.background.paper,
          maxWidth,
        }}
      >
        {hasError ? (
          /* Fallback when image fails to load — show alt text */
          <Stack
            sx={{
              gap: 2,
              p: 3,
              textAlign: 'center',
              color: palette.agentChat.message.agent.foreground.light,
            }}
            data-testid="image-preview-fallback"
          >
            <Typography
              variant="caption2"
              component="p"
              color="agentChat.message.agent.foreground.light"
            >
              Image failed to load
            </Typography>
            <Typography
              variant="body2"
              component="p"
              color="agentChat.message.agent.foreground.light"
            >
              {alt}
            </Typography>
          </Stack>
        ) : (
          <img
            src={src}
            alt={alt}
            onError={() => setHasError(true)}
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
            }}
            data-testid="image-preview-img"
          />
        )}
      </Box>
      {caption && (
        <Typography
          variant="caption2"
          component="p"
          sx={{
            color: palette.agentChat.message.agent.foreground.light,
            fontStyle: 'italic',
          }}
        >
          {caption}
        </Typography>
      )}
    </Stack>
  );
};
